/**
 * @fileoverview The share pictures' photographs, read once a session through
 * the API (never their signed URLs: the bucket sends no CORS headers, and a
 * drawn photo from it would taint the canvas). Moved out of
 * plant-list/lib/shareFrames (batch 18, A110); nothing in it changed.
 *
 * Where: the frames' drawing (features/share/frames) and, through
 * plant-list/lib/shareFrames, the season review's picture.
 * API: GET /photos/:plantObjId/:photoId/file?size= or GET
 * /locations/:id/photos/:photoId/file?size= (through apiClient, so the bearer
 * token goes with it).
 * Settings: SHARE_PHOTO_CACHE_SIZE, SHARE_PHOTO_FETCH_SIZE (config/tunables/plantList).
 *
 * @module framePhotos
 */

import { apiClient } from '@/shared/api/apiClient';
import { photoFilePath } from '@/shared/api/photos';
import { plantSubject, type Subject } from '@/shared/api/subject';
import { SHARE_PHOTO_CACHE_SIZE, SHARE_PHOTO_FETCH_SIZE } from '@/config/tunables/plantList';
import type { ShareFrameInput } from './frameOptions';

/** How many photographs are kept fetched: two full shares' worth. */
const PHOTO_CACHE_SIZE = SHARE_PHOTO_CACHE_SIZE;

/**
 * Photographs already fetched this session, by their path: a share made
 * again (another layout, a line typed, a photo tapped in) reads them from
 * here rather than the network. The large file (SHARE_PHOTO_FETCH_SIZE, about 1600px), a few hundred KB each.
 */
const photoBlobs = new Map<string, Promise<Blob>>();

/**
 * Reads one photo from this origin so a canvas may draw it, once a session.
 *
 * The stored photos sit behind signed URLs on a bucket that sends no CORS
 * headers, which is fine for an <img> and fatal here: drawing one taints the
 * canvas and toBlob() then refuses, so the frame could never be turned into a
 * file to share. The API streams the same bytes from an origin the browser
 * will let us read, and a blob is same-origin by definition.
 *
 * Through apiClient rather than fetch, because the API authenticates with a
 * Firebase bearer token that its interceptor attaches. A bare fetch carried no
 * token and passed only cookies — which the development server accepts,
 * because it mocks auth, and production refuses with a 401.
 *
 * @param subject - The plant or location the photograph belongs to.
 * @param photoId - The photograph's own id.
 * @returns The photograph, decoded; the caller closes it.
 */
export async function loadSubjectPhoto(subject: Subject, photoId: number): Promise<ImageBitmap> {
  const path = photoFilePath(subject, photoId);
  let blob = photoBlobs.get(path);
  if (!blob) {
    blob = apiClient.get<Blob>(path, { params: { size: SHARE_PHOTO_FETCH_SIZE }, responseType: 'blob' }).then((response) => response.data);
    photoBlobs.set(path, blob);
    // A failed read is not kept, so the next set asks again.
    blob.catch(() => { if (photoBlobs.get(path) === blob) photoBlobs.delete(path); });
    // The oldest goes once there are more than a long share's worth.
    while (photoBlobs.size > PHOTO_CACHE_SIZE) photoBlobs.delete(photoBlobs.keys().next().value as string);
  }
  return createImageBitmap(await blob);
}

/**
 * Reads one of the share's photographs (loadSubjectPhoto), from its plant
 * when it is a plant's photo in a location's share.
 *
 * @param input - The sharer's choices, for the subject and photoSource.
 * @param photoId - The photograph, as the share knows it (negative for a plant's photo in a location's share).
 * @returns The photograph, decoded; the caller closes it.
 */
export async function loadPhoto(input: ShareFrameInput, photoId: number): Promise<ImageBitmap> {
  // A plant's photo in a location's share comes from its plant.
  const source = input.photoSource?.(photoId) ?? null;
  const subject = source?.subject ?? input.subject ?? plantSubject(input.plantObjId);
  const bitmap = await loadSubjectPhoto(subject, source?.photoId ?? photoId);
  // Told to the studio, so a crop made in one frame shape can be refitted to another (B86).
  if (bitmap.height > 0) input.onPhotoShape?.(photoId, bitmap.width / bitmap.height);
  return bitmap;
}
